<template>
    <div class="request-list">
        <Table
            :columns="tblColumns"
            :data="requestsDataList"
            :loading="isLoading"
            no-data-text="No leave credit requests for this year"
            class="request-list__table"
        />

        <div class="request-list__cards">
            <Spin v-if="isLoading" fix />
            <p v-if="!requestsDataList.length" class="request-list__empty">No leave credit requests for this year</p>
            <div v-for="row in requestsDataList" :key="row._id" class="request-card">
                <div class="request-card__head">
                    <div>
                        <div class="request-card__dates">{{ formatType(row) }}</div>
                        <div class="request-card__meta">
                            Requested {{ formatCredit(row.credit_requested) }} · Approved
                            {{ formatCredit(row.credit_approved) }}
                        </div>
                    </div>
                    <span :class="['request-status', `request-status--${row.status}`]">
                        <span class="request-status__dot" />{{ formatStatus(row.status) }}
                    </span>
                </div>
                <div class="request-card__foot">
                    <span class="request-card__filed">Filed {{ formatDate(row.createdAt) }}</span>
                    <div class="request-actions">
                        <Button size="small" icon="md-eye" @click="handleView(row)">View</Button>
                        <template v-if="isPending(row)">
                            <Button size="small" icon="md-create" @click="handleEdit(row)">Edit</Button>
                            <Button
                                size="small"
                                icon="ios-remove-circle-outline"
                                class="request-actions__cancel"
                                @click="handleCancel(row)"
                            >
                                Cancel
                            </Button>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <Page
            v-if="totalCount > limit"
            :total="totalCount"
            :current="currentPage"
            :page-size="limit"
            size="small"
            class="request-list__pagination"
            @on-change="handleOnpageChanged"
        />
        <Create ref="CreditModal" @create="handleReloadList" />
        <DeleteRequestModal ref="DeleteRequest" @create="handleReloadList" />
    </div>
</template>

<script>
import moment from 'moment';
import Create from './create.vue';
import DeleteRequestModal from './DeleteRequestModal';

const CREDIT_TYPES = {
    'LEAVE|VL': 'Vacation Leave',
    'LEAVE|SL': 'Sick Leave',
    'LEAVE|AL': 'Appreciation Leave',
};

export default {
    components: {
        Create,
        DeleteRequestModal,
    },
    props: {
        isLoading: {
            type: Boolean,
            default: false,
        },
        requestsDataList: {
            type: Array,
            default: () => [],
        },
        currentPage: {
            type: Number,
            default: 1,
        },
        totalCount: {
            type: Number,
            default: 0,
        },
        limit: {
            type: Number,
            default: 10,
        },
    },

    data() {
        return {
            tblColumns: [
                {
                    title: 'Filed On',
                    key: 'createdAt',
                    minWidth: 120,
                    render: (h, params) => h('span', this.formatDate(params.row.createdAt)),
                },
                {
                    title: 'Type',
                    key: 'type',
                    minWidth: 150,
                    render: (h, params) => h('span', this.formatType(params.row)),
                },
                {
                    title: 'Credit Requested',
                    key: 'credit_requested',
                    minWidth: 140,
                    render: (h, params) => h('span', this.formatCredit(params.row.credit_requested)),
                },
                {
                    title: 'Approved Credit',
                    key: 'credit_approved',
                    minWidth: 140,
                    render: (h, params) =>
                        h(
                            'span',
                            { class: { 'request-list__muted': !params.row.credit_approved } },
                            this.formatCredit(params.row.credit_approved)
                        ),
                },
                {
                    title: 'Status',
                    key: 'status',
                    minWidth: 130,
                    render: (h, params) =>
                        h('span', { class: ['request-status', `request-status--${params.row.status}`] }, [
                            h('span', { class: 'request-status__dot' }),
                            this.formatStatus(params.row.status),
                        ]),
                },
                {
                    title: 'Actions',
                    width: 160,
                    align: 'right',
                    render: (h, params) => {
                        const iconButton = (content, icon, onClick, extraClass = '') =>
                            h('Tooltip', { props: { placement: 'top', content, transfer: true } }, [
                                h('Button', {
                                    class: ['request-actions__icon', extraClass],
                                    props: { icon, shape: 'circle' },
                                    on: { click: onClick },
                                }),
                            ]);
                        const buttons = [iconButton('View', 'md-eye', () => this.handleView(params.row))];
                        if (this.isPending(params.row)) {
                            buttons.push(
                                iconButton('Edit', 'md-create', () => this.handleEdit(params.row)),
                                iconButton(
                                    'Cancel',
                                    'ios-remove-circle-outline',
                                    () => this.handleCancel(params.row),
                                    'request-actions__cancel'
                                )
                            );
                        }
                        return h('div', { class: 'request-actions' }, buttons);
                    },
                },
            ],
        };
    },

    methods: {
        formatDate(date) {
            return date ? moment(date).utc().format('YYYY-MM-DD') : '—';
        },
        formatType(row) {
            if (!row.type) return '—';
            return CREDIT_TYPES[row.type.toUpperCase()] || row.type;
        },
        formatCredit(value) {
            if (value === null || value === undefined) return '—';
            return `${value} ${Number(value) === 1 ? 'day' : 'days'}`;
        },
        formatStatus(status) {
            if (!status) return '—';
            return status.charAt(0).toUpperCase() + status.slice(1);
        },
        isPending(row) {
            return !!row.status && row.status.includes('pending');
        },
        handleView(row) {
            this.$refs.CreditModal.openModalForReadOnly(row);
        },
        handleEdit(row) {
            this.$refs.CreditModal.openModalForEditing(row);
        },
        handleCancel(row) {
            this.$refs.DeleteRequest.openModal(row, 'cancel');
        },
        handleReloadList() {
            this.$emit('reload');
        },
        handleOnpageChanged(page) {
            this.$emit('on-page-change', page);
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../shared/request-table.scss';
</style>
